// @flow

import * as React from 'react';
import Logging from '@ta/common.logging';
import { ErrorBoundary } from '@ta/common.error-handling';
import { FeedLoadingSkeleton } from '@ta/social.skeletons';
import {
  MixerAPIQuery,
  CanBoostQuery,
  sessionType,
  QueryWithPartialRefetchUntilReactApollo220Release as Query,
} from '@ta/social.feed';
import {
  HomeFeedScrollPrompt,
  ScopedHomeLoggedOutUsersLoginBar,
  CheckNewFeed,
} from '@ta/social.educational-tooltips';
import type { ViewportCategory } from '@ta/common.state';
import { featureIsEnabled } from '@ta/common.features';
import { RenderErrorBanner } from '@ta/loading.error';
import { TrackingNamespaceContext } from '@ta/tracking.interactions';
import { getAllowedTypes, getAllowedTourismTypes } from '@ta/social.feed-filter';
import { Feed } from '@ta/social.feed-ui';

export type Props = {|
  placement: string,
  puid: string,
  locationId: number,
  loggedInUserId: string,
  latitude?: number,
  longitude?: number,
  newContentSubmittedTimestamp?: Date,
  allowedTypes: ?Array<string>,
  viewportCategory: ViewportCategory,
  changeTabCallback?: (tab: string) => void,
  isNearbyScope: boolean,
  tab?: string,
  feedIdToRestore: ?string,
  currencyCode?: string,
  renderBeforeFeed?: (refetch: () => Promise<*>) => React.Node,
  ipGeo: number,
  queryPlacement: string,
|};

const mixerNamespace = 'mixer';

export default class HomeMixerFeed extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.firstPage = true;

    // This is a hack; memorize feedIdToRestore.  feedIdToRestore is not present in the URL for SEO reasons, so it is much harder
    // to preserve it in the route state throughout the session.
    this.feedIdToRestore = props.feedIdToRestore;
  }

  /**
   * Set to true initially to indicate we're on the first page of the feed and need to
   * display Show More. The updateFeed() call sets this to false so we use infinite
   * scroll to display future pages after that.
   */
  firstPage: boolean;

  // See comment around its setting
  feedIdToRestore: ?string;

  topOfFeedRef: React.ElementRef<*> = React.createRef();

  refetch = React.createRef<?({}) => Promise<*>>();

  reloadFeed = () => {
    if (this.refetch.current) {
      return this.refetch.current({
        reset: true,
      });
    }

    return Promise.resolve(null);
  };

  render() {
    const allowedTypes =
      this.props.queryPlacement === 'TOURISM'
        ? getAllowedTourismTypes(this.props.allowedTypes)
        : getAllowedTypes(this.props.allowedTypes);
    const member = { userId: this.props.loggedInUserId, isMe: true };
    return (
      <ErrorBoundary>
        {this.props.renderBeforeFeed != null && this.props.renderBeforeFeed(this.reloadFeed)}

        <RenderErrorBanner>
          {renderErrorBanner => (
            <TrackingNamespaceContext.Provider value={mixerNamespace}>
              <Query
                query={MixerAPIQuery}
                errorPolicy="all"
                variables={{
                  placement: this.props.queryPlacement
                    ? this.props.queryPlacement
                    : this.props.placement,
                  locationId: this.props.locationId,
                  sessionType: sessionType(this.props.viewportCategory),
                  puid: this.props.puid,
                  allowedTypes,
                  latitude: this.props.latitude || null,
                  longitude: this.props.longitude || null,
                  reset: !this.feedIdToRestore,
                  restoreFromFeedId: this.feedIdToRestore,
                  socialReference: featureIsEnabled('social_at_referencing_profile_links'),
                  ipGeo: this.props.ipGeo,
                  currency: this.props.currencyCode,
                  prebuiltMediaCollection: featureIsEnabled('ml_geo_photo_sort_order_from_db')
                    ? 'ML_GEO_ROLLUP'
                    : null,
                }}
                onError={renderErrorBanner}
              >
                {({ loading, error, data = {}, refetch, fetchMore }) => {
                  // refetch is stored in a ref so it can be used outside of the Query. The mixer
                  // depends on server side state instead of client side variables, so it requires
                  // a manual callback when something updates that requires it to reload.
                  this.refetch.current = refetch;

                  // This query depends on the logged in user but the feed query can't depend on the logged in state due to its ephemeral state
                  // Have a nested query here but ensure it's either included in ssrQueries or duplicated elsewhere at the top level to "warm" the cache
                  return (
                    <Query
                      query={CanBoostQuery}
                      errorPolicy="all"
                      skip={!this.props.loggedInUserId}
                    >
                      {canBoostQueryResult => {
                        const loggedInMemberAuthorization =
                          canBoostQueryResult &&
                          canBoostQueryResult.data &&
                          canBoostQueryResult.data.authzInfo;
                        if (error) {
                          Logging.error(`Error fetching home feed: `, error);
                        }
                        if (loading) {
                          return <FeedLoadingSkeleton numSections={4} />;
                        }
                        const socialFeed = data ? data.socialFeed || data.socialFeedV2 : null;

                        return (
                          <div ref={this.topOfFeedRef}>
                            <Feed
                              userId={this.props.loggedInUserId}
                              member={member}
                              socialFeed={socialFeed}
                              refetch={refetch}
                              error={error}
                              fetchMore={fetchMore}
                              mixerPlacement={this.props.placement}
                              allowedTypes={allowedTypes}
                              locationId={this.props.locationId}
                              loggedInUserId={this.props.loggedInUserId}
                              viewportCategory={this.props.viewportCategory}
                              puid={this.props.puid}
                              feedIdToRestore={this.feedIdToRestore}
                              reset={this.firstPage || (socialFeed && socialFeed.feedWasReset)}
                              newContentSubmittedTimestamp={this.props.newContentSubmittedTimestamp}
                              loggedInMemberAuthorization={loggedInMemberAuthorization}
                            />
                            <HomeFeedScrollPrompt
                              elementToScrollTo={this.topOfFeedRef}
                              feedImpressionId={socialFeed && socialFeed.impressionId}
                            />
                            <ScopedHomeLoggedOutUsersLoginBar
                              topOfFeed={this.topOfFeedRef}
                              feedImpressionId={socialFeed && socialFeed.impressionId}
                            />
                            <CheckNewFeed
                              topOfFeed={this.topOfFeedRef}
                              reloadFeedCallback={refetch}
                              placement={this.props.placement}
                              puid={this.props.puid}
                              locationId={this.props.locationId}
                              latitude={this.props.latitude}
                              longitude={this.props.longitude}
                              feedIdToRestore={this.feedIdToRestore}
                              allowedTypes={this.props.allowedTypes}
                              ipGeo={this.props.ipGeo}
                            />
                          </div>
                        );
                      }}
                    </Query>
                  );
                }}
              </Query>
            </TrackingNamespaceContext.Provider>
          )}
        </RenderErrorBanner>
      </ErrorBoundary>
    );
  }
}
